import type { ProfanityLevel } from "../state";
import { censorText, getDailyNotification, localDayKey } from "./engine";

/**
 * The words on a reminder notification. Shared by the app (the test button
 * and the fallback reminder that fires when the app is opened) and by the
 * push server (server/push.ts), so a pushed reminder reads exactly like the
 * one the phone would have made itself.
 */
export interface ReminderEvent {
  id: string;
  name: string;
  personName: string;
  targetDate: string;
}

export interface ReminderMessage {
  title: string;
  body: string;
  /** Whole days left, counted like the big number on screen: what goes on the app icon badge. 0 in the last day and once it's here. */
  daysLeft: number;
}

/**
 * Engine calls read the calendar day with local getters. The server runs in
 * UTC, so it passes `wallClock`: a function that shifts a moment to the
 * phone's time zone's wall clock read as UTC. On the phone it's the identity.
 */
export type WallClock = (d: Date) => Date;
const identity: WallClock = (d) => d;

/**
 * Notifications always speak Spicy, whatever the dial on the countdown says
 * (the user's call): rude enough to be funny on a lock screen, not so rude
 * it's a problem when someone else sees it.
 */
export const NOTIFICATION_LEVEL: ProfanityLevel = 1;

/**
 * Whole days left, rounded down, exactly as the countdown's big number
 * counts them, so the badge and the notification never say one more than
 * the screen.
 */
export function wholeDaysLeft(targetDate: string, now = new Date()): number {
  return Math.floor(Math.max(0, new Date(targetDate).getTime() - now.getTime()) / 86_400_000);
}

/** "30 days till Stag Do": the days and the event are the headline. */
function headline(event: ReminderEvent, daysLeft: number): string {
  const name = event.name.trim();
  if (daysLeft === 0) return name ? `Under a day till ${name}` : "Under a day to go";
  const days = `${daysLeft} ${daysLeft === 1 ? "day" : "days"}`;
  return name ? `${days} till ${name}` : `${days} to go`;
}

export function dailyReminder(event: ReminderEvent, censorOn: boolean, now = new Date(), wallClock: WallClock = identity): ReminderMessage {
  const level = NOTIFICATION_LEVEL;
  const daysLeft = wholeDaysLeft(event.targetDate, now);
  const body = getDailyNotification(
    {
      level,
      eventId: event.id,
      eventName: event.name,
      personName: event.personName,
      target: wallClock(new Date(event.targetDate)),
      now: wallClock(now),
    },
    daysLeft
  );
  return { title: headline(event, daysLeft), body: censorText(body, level, censorOn), daysLeft };
}

export function reachedReminder(event: ReminderEvent): ReminderMessage {
  return { title: `${event.name.trim() || "The big day"} is here`, body: "No more counting. Go on then.", daysLeft: 0 };
}

export const NO_EVENT_TEST: ReminderMessage = { title: "C*UNTDOWN", body: "Notifications work. Now set a countdown.", daysLeft: 0 };

/** The phone's calendar day for `now`, as YYYY-MM-DD. */
export function dayKey(now: Date, wallClock: WallClock = identity): string {
  return localDayKey(wallClock(now));
}
